import type { Dispatch, SetStateAction } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import OriginTracer from "@atlassiansox/origin-tracing";

import { Cookie } from "@/utils/cookie";
import { experiments, getExperimentWithExposures } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { useLoggedInUser } from "../../../../utils/logged-in-user-provider";

import { LoggedInUser } from "./components/loggedin-user-view";
import { LoggedOutUser } from "./components/loggedout-user-view";
import { LoadingSpinner } from "./components/spinner";
import { getAppFromPath } from "./util";

export const analyticsLabels = {
    "JoinSiteUserDropdown.logOut": "Log out",
    "JoinSiteUserDropdown.logIn": "Sign in",
    "JoinSiteUserDropdown.joinTeam": "Find my team",
    "JoinSiteUserDropdown.profile": "Profile",
    "JoinSiteUserDropdown.switchAccount": "Switch Account",
    "JoinSiteUserDropdown.licenses": "Licenses"
} as const;

const NEXT_PUBLIC_API_MAC_URL = process.env["NEXT_PUBLIC_API_MAC_URL"];
const NEXT_PUBLIC_IDENTITY_BASE_URL =
    process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"];
const NEXT_PUBLIC_API_PRIVATE_BASE_URL =
    process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];

export type GlobalNavUserProfileProps = {
    readonly isUseNewLoggedInUserDropdown?: boolean;
    readonly isUseGeneralSigninPromptConfig?: boolean;
    readonly positionTryNowCtaOnRight?: boolean;
    readonly setIsUseGeneralSigninPromptConfig?: Dispatch<
        SetStateAction<boolean>
    >;
};

export type useGlobalNavUserProfileClickActionsReturnType = {
    readonly onLogOutClick: () => void;
    readonly onManageLicensesClick: () => void;
    readonly onManageProfileClick: () => void;
    readonly onSwitchAccountClick: () => void;
    readonly onLogInClick: () => void;
};

const generalSigninPromptParentDivStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});

export const useGlobalNavUserProfileClickActions = ({
    email
}: {
    email?: string;
}): useGlobalNavUserProfileClickActionsReturnType => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const onManageLicensesClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            name: "bxp.globalnavdropdown.click.manage.licenses",
            label: analyticsLabels["JoinSiteUserDropdown.licenses"]
        }).fire();
        window.location.assign(NEXT_PUBLIC_API_MAC_URL as string);
    };

    const getManageProfileUrl = (userEmail: string) => {
        const applicationParam = encodeURIComponent("wac");
        const continueUrlParam = encodeURIComponent(
            `${NEXT_PUBLIC_IDENTITY_BASE_URL}/manage-profile`
        );
        const loginHintParam = encodeURIComponent(userEmail);

        return `${NEXT_PUBLIC_IDENTITY_BASE_URL}/login?application=${applicationParam}&continue=${continueUrlParam}&login_hint=${loginHintParam}`;
    };
    const onManageProfileClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            name: "bxp.globalnavdropdown.click.manage.profile",
            label: analyticsLabels["JoinSiteUserDropdown.profile"]
        }).fire();

        window.location.assign(getManageProfileUrl(email || ""));
    };

    const getLogoutUrl = () => {
        const continueUrl = window.location.href;
        return `${NEXT_PUBLIC_IDENTITY_BASE_URL}/logout?continue=${encodeURIComponent(
            continueUrl
        )}`;
    };
    const onLogOutClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            name: "bxp.globalnavdropdown.click.logout",
            label: analyticsLabels["JoinSiteUserDropdown.logOut"]
        }).fire();

        if (window.location.hostname === "localhost") {
            Cookie.writeCookie("login_user_detected", "false", 3650, true);
        }

        window.location.assign(getLogoutUrl());
    };

    const onLogInClick = () => {
        const baseUrl = NEXT_PUBLIC_IDENTITY_BASE_URL;
        const stargateBaseUrl = NEXT_PUBLIC_API_PRIVATE_BASE_URL;
        let continueUrl = stargateBaseUrl + "/start/authredirect";

        const keepLandUsersOnWacExperiment = getExperimentWithExposures(
            experiments.cfind4026KeepLandUsersOnWac
        );

        // if treatment return the new continue url, else control gets whatever normal route behavior is
        if (
            keepLandUsersOnWacExperiment.type == "enrolled" &&
            keepLandUsersOnWacExperiment.value
        ) {
            continueUrl =
                stargateBaseUrl +
                `/start/authredirect?continue=${window.location.href}`;
        }

        // Check if the user is on localhost and set the cookie for the domain localhost
        if (window.location.hostname === "localhost") {
            Cookie.writeCookie("login_user_detected", "true", 3650, true);
        }

        const jiraOrConfluenceApp = getAppFromPath(
            globalThis.location ? globalThis.location.pathname : ""
        );
        if (
            jiraOrConfluenceApp &&
            checkFeatureFlagGate(featureFlags.twcg790AATest)
        ) {
            const originTracer = new OriginTracer({
                product: "marketing"
            });
            let identityUrl = `${baseUrl}/login?continue=${encodeURIComponent(
                continueUrl
            )}`;
            identityUrl = originTracer.addToUrl(identityUrl);

            createAnalyticsEvent({
                action: "clicked",
                name: "bxp.globalnavdropdown.click.login",
                label: analyticsLabels["JoinSiteUserDropdown.logIn"],
                attributes: {
                    ...originTracer.toAnalyticsAttributes(),
                    application: jiraOrConfluenceApp,
                    flagKey: "twcg-790-contextual-background-aa",
                    cohort: "variation"
                }
            }).fire();

            window.location.assign(identityUrl);
            return;
        }

        createAnalyticsEvent({
            action: "clicked",
            name: "bxp.globalnavdropdown.click.login",
            label: analyticsLabels["JoinSiteUserDropdown.logIn"],
            attributes: {
                flagKey: "twcg-790-contextual-background-aa",
                cohort: "control"
            }
        }).fire();

        window.location.assign(
            `${baseUrl}/login?continue=${encodeURIComponent(continueUrl)}`
        );
    };

    const getSwitchAccountURL = (continueUrlParam?: string) => {
        const continueUrl = continueUrlParam
            ? continueUrlParam
            : window.location.href;
        return `${NEXT_PUBLIC_IDENTITY_BASE_URL}/login/select-account?continue=${encodeURIComponent(
            continueUrl
        )}`;
    };
    const onSwitchAccountClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            name: "bxp.globalnavdropdown.click.switchAccount",
            label: analyticsLabels["JoinSiteUserDropdown.switchAccount"]
        }).fire();

        window.location.assign(getSwitchAccountURL());
    };

    return {
        onSwitchAccountClick,
        onManageProfileClick,
        onManageLicensesClick,
        onLogOutClick,
        onLogInClick
    };
};

export const GlobalNavUserProfile = ({
    isUseNewLoggedInUserDropdown = false,
    isUseGeneralSigninPromptConfig = false,
    setIsUseGeneralSigninPromptConfig
}: GlobalNavUserProfileProps) => {
    const userInfo = useLoggedInUser();

    const {
        onSwitchAccountClick,
        onManageProfileClick,
        onManageLicensesClick,
        onLogOutClick,
        onLogInClick
    } = useGlobalNavUserProfileClickActions({ email: userInfo.email });

    if (userInfo.requestInProgress) {
        return LoadingSpinner;
    }

    return (
        <div
            css={[
                isUseGeneralSigninPromptConfig &&
                    generalSigninPromptParentDivStyle
            ]}
        >
            {userInfo.isUserLoggedIn ? (
                <LoggedInUser
                    userInfo={{
                        displayName: userInfo.name || "",
                        email: userInfo.email || "",
                        avatar: userInfo.picture || ""
                    }}
                    onSwitchAccountClick={onSwitchAccountClick}
                    onManageProfileClick={onManageProfileClick}
                    onManageLicensesClick={onManageLicensesClick}
                    onLogOutClick={onLogOutClick}
                    isUseNewLoggedInUserDropdown={isUseNewLoggedInUserDropdown}
                />
            ) : (
                <LoggedOutUser
                    onLogInClick={onLogInClick}
                    isUseGeneralSigninPromptConfig={
                        isUseGeneralSigninPromptConfig
                    }
                    setIsUseGeneralSigninPromptConfig={
                        setIsUseGeneralSigninPromptConfig
                    }
                />
            )}
        </div>
    );
};
